import React, { useState, useRef, useEffect } from 'react';
import { Home as HomeIcon, Truck, Scissors } from 'lucide-react';
import { useReveal, JoinBlock, InstagramSection, Ticker } from '../components/shared';
import { IMAGES, TICKER_ITEMS, TUBERS } from '../mock';
import { subscribeEmail } from '../api';

export default function Home({ go, scrollToFlowers }) {
  useReveal([]);
  const flowersRef = useRef(null);
  useEffect(() => {
    if (scrollToFlowers && flowersRef.current) setTimeout(() => flowersRef.current.scrollIntoView({ behavior: 'smooth' }), 80);
  }, [scrollToFlowers]);
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  const carousel = useRef(null);
  const submit = async () => {
    if (!email.includes('@')) return;
    setJoined(true); const val = email; setEmail('');
    try { await subscribeEmail(val, 'home-hero'); } catch (e) { /* still show success */ }
  };
  const scroll = (dir) => carousel.current && carousel.current.scrollBy({ left: dir * 220, behavior: 'smooth' });

  return (
    <div className="page active">
      {/* HERO */}
      <div className="hero">
        <img src={IMAGES.hero} className="hero-img" alt="Florist holding a bouquet of dahlias" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 42%' }} />
        <div className="hero-overlay"></div>
        <div className="hero-content" style={{ gridTemplateColumns: '1fr' }}>
          <div className="hero-left">
            <span className="hero-eyebrow">Est. on the Surf Coast Victoria</span>
            <h1 className="hero-headline">Seasonal blooms,<em>grown by the sea.</em></h1>
            <p className="hero-body">Nestled on the edge of Torquay, right where the Great Ocean Road begins, Torquay Flower Farm is our little piece of magic — where we grow seasonal blooms, embrace slow living, and chase a dream worth sharing.</p>
            <div className="hero-form-row">
              <input type="email" className="hero-form-input" placeholder="your@email.com" aria-label="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
              <button className="hero-form-submit" style={joined ? { background: 'var(--forest)', color: '#fff' } : {}} onClick={submit}>{joined ? "You're on the list! ✦" : 'Join Our Community'}</button>
            </div>
            <p className="hero-form-note">First access to dahlia tubers · No spam, unsubscribe any time.</p>
          </div>
        </div>
      </div>

      <Ticker items={TICKER_ITEMS} />

      {/* BRAND STATEMENT */}
      <section className="home-intro">
        <span className="eyebrow rev">Torquay · Surf Coast Victoria</span>
        <h2 className="home-intro-h rev">A small farm with a big love<br /><em>for growing beautiful things.</em></h2>
        <p className="home-intro-p rev">We grow seasonal flowers built for salt air and sandy soil — dahlias, cosmos and ranunculus, cut fresh from the paddock. Everything here moves gently with the seasons, and so do we.</p>
      </section>

      {/* FLOWERS */}
      <section className="home-flowers" ref={flowersRef} id="flowers" data-testid="home-flowers">
        <div className="home-ways">
          {[[HomeIcon, 'Farm Gate', 'Dahlias, cosmos and ranunculus, picked in the cool of the morning and set out at the farm gate the same day — small batches, honest growing, and whatever the paddock is giving that week. In season January to April; we post what\'s out each morning via Instagram stories @southbeachblooms.', 'Seasonal flowers, farm-gate fresh'], [Truck, 'Wholesale', 'Fresh-cut dahlias, cosmos and ranunculus for local florists and event designers, harvested to order and sold by the bunch — five stems per bunch. As a new and growing farm we keep our wholesale list small, so quality stock is there for our clients every week.', 'Flowers by the bunch, for the trade'], [Scissors, 'Pick Your Own', 'Coming soon. A relaxed wander through the dahlia patch, snips in hand — pick and gather your own bunch to take home. We\'re trialling small, seasonal sessions kept intimate so there\'s plenty of flowers (and space) for everyone. Dates go to our community list first.', 'Cut your own bunch, in season']].map(([Ic, t, d, sub], i) => (
            <div className="home-way rev" key={i} data-testid={`home-way-${i}`}>
              <span className="offer-icon"><Ic size={26} strokeWidth={1.3} /></span>
              <div className="home-way-t">{t}</div>
              {sub && <div className="home-way-sub">{sub}</div>}
              <p className="home-way-d">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EDITORIAL — DAHLIA TUBERS */}
      <div className="split home-split circ pink-panel" data-testid="home-tubers-panel">
        <div className="split-copy" style={{ order: 1 }}>
          <span className="eyebrow rev">For The Dahlia Obsessed</span>
          <h2 className="home-split-h rev">Dahlia tubers,<em>grown by us</em></h2>
          <p className="body-text rev">Hand-dug, inspected and stored the way tubers should be — rare, ridiculously good varieties raised right here on the farm, that turn a single tuber into a whole summer of blooms.</p>
          <div className="tp-facts rev" data-testid="tubers-facts">
            <span>Sale opens September 2027</span><i>·</i><span>Ships Aus-wide excl. TAS/WA</span><i>·</i><span>Hand-dug in Torquay</span>
          </div>
          <div className="rev"><button className="btn btn-outline tp-btn" onClick={() => go('tubers')} data-testid="tubers-browse-btn">Browse the 18 Varieties →</button></div>
        </div>
        <div className="split-photo rev" style={{ order: 2, '--circ-img': 'url(/brand/tubers-home.webp)' }} />
      </div>

      {/* VARIETY GALLERY */}
      <section className="home-varieties">
        <span className="eyebrow rev" style={{ textAlign: 'center', display: 'block' }}>Eighteen varieties, colour by colour</span>
        <h2 className="home-varieties-h rev">This season&rsquo;s favourites</h2>
        <div className="wwd2-carousel-wrap">
          <button className="wwd2-arrow" onClick={() => scroll(-1)} aria-label="Previous varieties">‹</button>
          <div className="wwd2-scroll" ref={carousel}>
            {TUBERS.map((t, i) => (
              <div className="wwd2-tcard" key={i}>
                <div className="wwd2-timg bg-photo" style={{ backgroundImage: `url(${t.img})` }} />
                <div className="wwd2-tname">{t.name}</div>
                <div className="wwd2-tprice">{t.price}</div>
              </div>
            ))}
          </div>
          <button className="wwd2-arrow" onClick={() => scroll(1)} aria-label="Next varieties">›</button>
        </div>
      </section>

      {/* QUOTE */}
      <div className="quote-block">
        <span className="eyebrow rev">Grown with care</span>
        <blockquote className="quote-text rev">&ldquo;What started as a backyard hobby has grown into a dream &mdash; sharing our passion for flowers, slower days in nature, and being a part of a wonderful, supportive community at the edge of the Great Ocean Road.&rdquo;</blockquote>
        <cite className="quote-attr rev">Laura &amp; Matt · Torquay Flower Farm</cite>
      </div>

      <JoinBlock
        eyebrow="Inaugural Dahlia Sale · September 2027"
        headline="We sell to our community first."
        body="Before the public. Before it sells out. Join the list now and be first through the gate when our inaugural dahlia tuber sale opens September 2027."
      />

      <InstagramSection eyebrow="Follow the journey" />
    </div>
  );
}
